import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useIsGivingPage } from "Flocknote/stateHooks";

import {
  useContactUs,
  usePrayerRequest,
} from "CrossFeatureComponents/MemberToolbar/API/queries";
import { useMemberToobarContext } from "CrossFeatureComponents/MemberToolbar/MemberToolbarState";
import { contentKeys } from "CrossFeatureComponents/MemberToolbar/contentKeys";
import { useMemberToolbarStore } from "CrossFeatureComponents/MemberToolbar/memberToolbarStore";

import stopEvents from "Utilities/stopEvents";

/**
 * @typedef OnSubmitHookReturn
 * @property {boolean} isLoading
 * @property {function} onSubmit
 * @property {string} errorMessage
 * @property {boolean} showError
 * @property {function} setShowError
 */

/**
 * @returns {OnSubmitHookReturn}
 */
const useOnSubmit = () => {
  const { setActiveContentKey } = useMemberToolbarStore();
  const {
    setMessage,
    prayerRequest,
    contactButtonRef,
    generalInquiry,
    flocknoteSupport,
    message,
  } = useMemberToobarContext();
  const { signupListID, noteID } = useParams();
  const givingInquiry = useIsGivingPage();

  const {
    mutateAsync: contactUs,
    errorMessage: contactErrorMessage,
    showError: showContactErrorMessage,
    setShowError: setContactShowError,
  } = useContactUs({
    givingInquiry,
    generalInquiry,
    flocknoteSupport,
    message,
    signupListID,
    noteID,
  });
  const {
    mutateAsync: sendPrayerRequest,
    errorMessage: prayerErrorMessage,
    showError: showPrayerErrorMessage,
    setShowError: setPrayerShowError,
  } = usePrayerRequest();
  const [isLoading, setIsLoading] = useState();

  const errorMessage = useMemo(
    () => prayerErrorMessage || contactErrorMessage,
    [contactErrorMessage, prayerErrorMessage]
  );

  const showError = useMemo(
    () => showPrayerErrorMessage || showContactErrorMessage,
    [showContactErrorMessage, showPrayerErrorMessage]
  );

  const setShowError = useCallback(
    (newValue) => {
      setContactShowError(newValue);
      setPrayerShowError(newValue);
    },
    [setContactShowError, setPrayerShowError]
  );

  const onSubmit = useCallback(
    async (e) => {
      setIsLoading(true);
      stopEvents(e);
      try {
        if (prayerRequest) await sendPrayerRequest();
        else await contactUs();
        setActiveContentKey(contentKeys.messageSent);
        // clear message after animation finishes since this component is kept in the DOM.
        setTimeout(() => setMessage(""), 300);
        contactButtonRef.current.focus();
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      contactUs,
      prayerRequest,
      sendPrayerRequest,
      setActiveContentKey,
      setMessage,
      contactButtonRef,
    ]
  );

  return { onSubmit, isLoading, errorMessage, showError, setShowError };
};

export { useOnSubmit };
